import { useEffect, useRef } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { LightProbeGrid } from '../lib/lightProbeGrid'
import { lightProbeGridCandidates } from '../lib/lightProbeGridKey'
import { fetchFirstOk } from '../lib/navmeshGridKey'
import { resolveAvatarTransformTarget } from '../lib/avatarTransformTarget.js'
import { fetchWithRetry } from '../lib/fetchRetry'
import { useLiveTune } from '../hooks/useLiveTune'

/**
 * Loads the baked SH grid for the current splat and lights the avatar with a
 * single THREE.LightProbe whose coefficients are trilinearly interpolated from
 * the grid by the avatar's world position every frame. Diffuse only (Phase A,
 * ENG-561).
 *
 * Off the React hot path: grid + probe + scratch buffers live in refs; useFrame
 * mutates the probe's SH coefficients in place (no per-frame allocation, no
 * React state). Renders nothing.
 *
 * No silent fallback (CLAUDE.md UX rule): a missing grid surfaces as
 * window.__LIGHT_PROBE_GRID__.state === 'no-cache' with a console pointer to
 * the bake command — never fake lighting.
 */
export default function LightProbeVolume({ splatUrl, splatVersion, reloadKey = 0 }) {
  const scene = useThree(s => s.scene)
  const tune = useLiveTune()
  const { lightProbeEnabled, lightProbeIntensity } = tune.lighting
  const gridRef = useRef(null)
  const probeRef = useRef(null)
  const shBuf = useRef(new Float32Array(27))
  const tmpVec = useRef(new THREE.Vector3())
  // Effective THREE.LightProbe.intensity = enabled ? multiplier : 0. Held in a
  // ref so the async grid-load closure can read the current value when it
  // installs the probe (the probe is created after this component mounts). The
  // effect below applies live edits to an already-installed probe; between the
  // two, no per-frame work is needed. Enable flag zeroes the contribution
  // without unmounting the probe.
  const intensityRef = useRef(lightProbeEnabled ? lightProbeIntensity : 0)
  useEffect(() => {
    intensityRef.current = lightProbeEnabled ? lightProbeIntensity : 0
    if (probeRef.current) probeRef.current.intensity = intensityRef.current
  }, [lightProbeEnabled, lightProbeIntensity])

  useEffect(() => {
    let cancelled = false
    const setState = (state, extra) => {
      if (typeof window !== 'undefined') window.__LIGHT_PROBE_GRID__ = { state, ...extra }
    }
    if (!splatUrl) { setState('idle'); return }
    setState('pending')

    ;(async () => {
      // ?v=<splatVersion> busts the proxy/browser cache after a (re)bake so the
      // freshly-baked grid is fetched, not a stale 404/old copy.
      const bust = splatVersion ? `?v=${encodeURIComponent(splatVersion)}` : ''
      const urls = await lightProbeGridCandidates(splatUrl)
      if (cancelled) return
      if (!urls.length) {
        console.warn(`[LightProbeVolume] ${splatUrl} has no baked grid`)
        setState('no-cache', { url: null })
        return
      }

      const { url, res } = await fetchFirstOk(urls, bust) // retries transient 5xx (AAM blip), not 404; revalidates via ETag so a re-bake shows on plain reload
      if (cancelled) return
      if (!res || !res.ok) {
        console.warn(`[LightProbeVolume] the server listed a grid at ${url} but it 404s — the row and the object disagree, so a re-bake is the fix.`)
        setState('no-cache', { url })
        return
      }
      // Read text first so a parse failure can report WHAT the body was: an
      // HTML head means SPA-fallback / proxy error page (a routing problem);
      // a JSON head with a short length means the body was TRUNCATED in
      // flight (connection dropped mid-stream under load) — two different
      // bugs that a bare "non-JSON body" can't separate. Loud failure should
      // carry the evidence.
      const text = await res.text()
      let data
      try { data = JSON.parse(text) } catch {
        setState('no-cache', { url, bodyLen: text.length, bodyHead: text.slice(0, 60) })
        return
      }
      if (cancelled) return
      gridRef.current = new LightProbeGrid(data)
      const probe = new THREE.LightProbe()
      probe.intensity = intensityRef.current // honour the live gate/multiplier on install
      scene.add(probe)
      probeRef.current = probe
      if (typeof window !== 'undefined') window.__LIGHT_PROBE__ = probe
      setState('applied', { url, probeCount: data.probes.length })
      console.log(`[LightProbeVolume] applied grid from ${url} (${data.probes.length} probes)`)
    })().catch(err => {
      if (cancelled) return
      console.error('[LightProbeVolume] failed:', err)
      setState('error', { error: String(err) })
    })

    return () => {
      cancelled = true
      if (probeRef.current) { scene.remove(probeRef.current); probeRef.current = null }
      gridRef.current = null
      if (typeof window !== 'undefined') { delete window.__LIGHT_PROBE_GRID__; delete window.__LIGHT_PROBE__ }
    }
    // reloadKey bumps when a bake for this env completes (bakeReload.js) → re-run
    // so the freshly-baked grid loads without a manual env re-select (was the
    // "lighting ready but balls black" bug — the volume never refetched after
    // no-cache). splatVersion still re-runs it on a splat re-upload.
  }, [splatUrl, splatVersion, scene, reloadKey])

  useFrame(() => {
    const grid = gridRef.current, probe = probeRef.current
    if (!grid || !probe) return
    // __AVATAR_PROBE_POS__ overrides the sample point (tests + dev oracle);
    // otherwise sample at the avatar's world position, raised to body height.
    const override = (typeof window !== 'undefined' && window.__AVATAR_PROBE_POS__) || null
    let x, y, z
    if (override) {
      x = override[0]; y = override[1]; z = override[2]
    } else {
      // WHICHEVER avatar is mounted, and where it IS — not where it started.
      // This read used to be `__AVATAR_SCENE__`, the GLB AvatarModel's own scene,
      // which ONLY that path publishes; a splat or stream character fell through
      // to the origin. That was written when a splat mounted at the origin and
      // stayed there, and it has not been true since movement drove the splat
      // root (walk-here, MoveTo*, the transform gizmo): the probe kept sampling
      // (0, 1.2, 0) for the life of the session, so the avatar could walk across
      // a room and carry the lighting of the spot it spawned on — which reads as
      // "the shading happened once and froze".
      //
      // resolveAvatarTransformTarget is the codebase's one answer to "where is
      // the avatar": __AVATAR_GROUP__ when a GLB rig is up, else whichever splat
      // root the mounted render path published. The same resolver NavMeshVolume
      // needed for the same reason.
      const root = resolveAvatarTransformTarget()
      if (root) root.getWorldPosition(tmpVec.current)
      else tmpVec.current.set(0, 0, 0)
      // The grid's lowest layer sits above the floor, so an un-raised origin can
      // fall outside the volume and interpolate to zero — the raise is what
      // lands the sample inside it.
      x = tmpVec.current.x; y = tmpVec.current.y + 1.2; z = tmpVec.current.z
    }
    const sh = grid.sampleSHInto(shBuf.current, x, y, z)
    const coeffs = probe.sh.coefficients
    for (let i = 0; i < 9; i++) coeffs[i].set(sh[i * 3], sh[i * 3 + 1], sh[i * 3 + 2])
  })

  return null
}
